import {DOCUMENT} from '@angular/common';
import {Component, EventEmitter, Inject, Input, OnChanges, OnInit, Output, SimpleChanges} from '@angular/core';
import {FormBuilder, Validators} from '@angular/forms';
import {AddFreeZonePattern} from '@application/command/add-free-zone-pattern';
import {AddRestZonePattern} from '@application/command/add-rest-zone-pattern';
import {RouteUtils} from '@application/helper/routing/route-utils';
import {CustomSettings} from '@domain/custom-settings/custom-settings';
import {Pattern} from '@domain/pattern';
import {PositionOfDialog} from '@domain/position-of-dialog';
import {BuggyForProductionSchedule} from '@domain/production-schedule/buggy-for-production-schedule';
import {CreelForProductionSchedule} from '@domain/production-schedule/creel-for-production-schedule';
import {ProductionOrderCustomSettings} from '@domain/production-schedule/custom-settings/production-order-custom-settings';
import {FinishingForProductionSchedule} from '@domain/production-schedule/finishing-for-production-schedule';
import {InitialFreeZone} from '@domain/production-schedule/free-zone/initial-free-zone';
import {MachineQualityForProductionSchedule} from '@domain/production-schedule/machine-quality-for-production-schedule';
import {MachineSummary} from '@domain/production-schedule/machine-summary';
import {ProductionSchedule} from '@domain/production-schedule/production-schedule';
import {InitialRestZone} from '@domain/production-schedule/rest-zone/initial-rest-zone';
import {RestZonePosition} from '@domain/production-schedule/rest-zone/rest-zone-position.enum';
import {UserDefinedRest} from '@domain/production-schedule/rest-zone/user-defined-rest';
import {Permission} from '@domain/profile/permission.enum';
import {Subscription as ProfileSubscription} from '@domain/profile/subscription';
import {OverviewListColoredYarnSetWithStartDent} from '@domain/textile-data/creel/overview-list-colored-yarn-set-with-start-dent';
import {Authentication, AUTHENTICATION} from '@infrastructure/http/authentication/authentication';
import {HttpBuggyCustomSettingsService} from '@infrastructure/http/buggy/http-buggy-custom-settings.service';
import {RepositionDialogComponent} from '@presentation/components/reposition-dialog/reposition-dialog.component';
import {SelectBuggyComponent} from '@presentation/components/select-buggy/select-buggy.component';
import {SelectFinishingComponent} from '@presentation/components/select-finishing/select-finishing.component';
import {PatternType} from '@presentation/components/select-pattern/pattern-type.enum';
import {SelectPatternComponentMatDialogConfigDataInterface} from '@presentation/components/select-pattern/select-pattern-component-mat-dialog-config.interface';
import {SelectPatternComponent} from '@presentation/components/select-pattern/select-pattern.component';
import {AddProductionSchedule} from '@presentation/pages/texfab/production-schedule/add/add-production-schedule';
import {ADD_PRODUCTION_SCHEDULE} from '@presentation/pages/texfab/production-schedule/add/add-production-schedule.service';
import {FinishingDetailsComponent} from '@presentation/pages/texfab/production-schedule/finishing-details/finishing-details.component';
import {OverviewListFinishing} from '@presentation/pages/textile-data/finishing/overview/overview-list-finishing';
import {WeaveQualityType} from '@presentation/pages/textile-data/machine-quality/overview/weave-quality-type.enum';
import {AssertionUtils, BaseComponent, canShowErrorForErrorCodeAndFormControlForFormGroup, DialogBuilderFactoryService, FormValidationHelper, TranslateService} from '@vdw/angular-component-library';
import {first, isEmpty, isEqual, isNil, isUndefined, multiply} from 'lodash-es';
import {DateTime} from 'luxon';
import {Subscription} from 'rxjs';
import {debounceTime, distinctUntilChanged, takeUntil} from 'rxjs/operators';
import {PRODUCTION_SCHEDULE_PLAN, ProductionSchedulePlan} from '../../plan/production-schedule-plan';
import {AdditionalOptionsForm, FreeZoneForm, RestZoneForm, UserDefinedRestForm} from './additional-options-form';

@Component({
  selector: 'app-production-schedule-initial-preparations-additional-options, [app-production-schedule-initial-preparations-additional-options]',
  templateUrl: './production-schedule-initial-preparations-additional-options.component.html',
  styleUrls: ['./production-schedule-initial-preparations-additional-options.component.scss']
})
export class ProductionScheduleInitialPreparationsAdditionalOptionsComponent extends BaseComponent implements OnChanges, OnInit {
  private static readonly restZonePatternFormControlName = 'restZone.pattern';
  private static readonly restZonePositionFormControlName = 'restZone.position';
  public readonly PATTERN_CONDITIONAL_CHIP_VALUE_KEY = `${this.translate.instant('GENERAL.PATTERN.PATTERN', {count: 1})} %(id)`;
  public readonly FINISHING_VIEW_PERMISSION = RouteUtils.paths.texStyle.finishingTemplate.editFinishingTemplate.requiredPermission;

  @Input() public machine: MachineSummary;
  @Input() public machineQuality: MachineQualityForProductionSchedule;
  @Input() public creel: CreelForProductionSchedule;
  @Input() public coloredYarnSets: OverviewListColoredYarnSetWithStartDent[];
  @Input() public freeZone: InitialFreeZone;
  @Input() public restZone: InitialRestZone;
  @Input() public finishing: FinishingForProductionSchedule;
  @Input() public dueDate: Date;
  @Input() public comment: string;
  @Input() public buggy: BuggyForProductionSchedule;
  @Input() public isRestZoneConfigurable: boolean;
  @Input() public factorForConversionFromDentsToMillimeters: number;
  @Input() public customSettings: ProductionOrderCustomSettings;
  @Input() public needsToShowValidationFeedback = true;
  @Input() public defaultInitialRestZones: InitialRestZone;
  @Input() public weaveQualityType: WeaveQualityType;
  @Input() public areAllFieldsDisabled: boolean = false;
  @Input() public productionSchedule: ProductionSchedule;
  @Output() public formValuesChanged: EventEmitter<void> = new EventEmitter<void>();
  @Output() public restZoneLoaded = new EventEmitter<void>();
  @Output() public freeZoneLoaded = new EventEmitter<void>();
  @Output() public generateName = new EventEmitter<void>();

  public additionalOptionsForm: AdditionalOptionsForm;
  public scheduleDueDatePermission: Permission = Permission.SCHEDULE_DUE_DATE;
  public newBuilderPermission: Permission = Permission.TEXFAB_NEW_BUILDER;
  public possibleRestZonePositions: RestZonePosition[];
  public possibleRestZoneUserDefinedPositions: RestZonePosition[];
  public finishingBaseRouterLink = RouteUtils.paths.texStyle.machineQuality.editFinishingFromQuality.absolutePath;

  private additionalOptionsFormValueChangesSubject: Subscription;
  private currentSubscription: ProfileSubscription;
  private hasBuggyNameGenerator: boolean;

  private readonly formValidationHelper = new FormValidationHelper();

  public constructor(
    @Inject(ADD_PRODUCTION_SCHEDULE) private readonly addProductionSchedule: AddProductionSchedule,
    @Inject(DOCUMENT) private readonly document: Document,
    @Inject(AUTHENTICATION) private readonly authentication: Authentication,
    @Inject(PRODUCTION_SCHEDULE_PLAN) private readonly productionSchedulePlan: ProductionSchedulePlan,
    private readonly httpBuggyCustomSettings: HttpBuggyCustomSettingsService,
    private readonly dialogBuilderFactoryService: DialogBuilderFactoryService,
    private readonly formBuilder: FormBuilder,
    private readonly translate: TranslateService,
    private readonly addRestZonePattern: AddRestZonePattern,
    private readonly addFreeZonePattern: AddFreeZonePattern
  ) {
    super();
  }

  public ngOnInit(): void {
    this.currentSubscription = this.authentication.getCurrentSubscription();

    if (this.machine.canHaveRestAndFreeZonesAndPathLabels()) {
      this.possibleRestZonePositions = RestZonePosition.getRestZonePositions();
      this.possibleRestZoneUserDefinedPositions = RestZonePosition.getUserDefinedRestZonePositions();
    }

    this.finishingBaseRouterLink = RouteUtils.paths.texStyle.machineQuality.editFinishingFromQuality.absolutePath.replace(':qualityId', this.machineQuality.id.toString());
    this.setFormFields();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (!AssertionUtils.isNullOrUndefined(this.additionalOptionsForm)) {
      if ('buggy' in changes && !changes.buggy.isFirstChange()) {
        this.updateBuggyFormField();
      }

      if ('finishing' in changes && !changes.finishing.isFirstChange()) {
        this.updateFinishingFormField();
      }

      if ('freeZone' in changes && !changes.freeZone.isFirstChange()) {
        this.updateFreeZoneFormFields();
      }

      if (this.shouldUpdateRestZoneFormFields(changes)) {
        this.updateFormFieldsForRestZone();
      }

      if (this.needsToShowValidationFeedback) {
        this.formValidationHelper.checkForm(this.additionalOptionsForm, this.document);
      }
    }
  }

  public isUsingNewBuilder(): boolean {
    return !AssertionUtils.isNullOrUndefined(this.currentSubscription) && this.currentSubscription?.hasPermission(this.newBuilderPermission);
  }

  public canSelectBuggy(): boolean {
    return !this.isUsingNewBuilder() && this.currentSubscription?.hasPermission(Permission.VIEW_BUGGIES);
  }

  public onDateChange(): void {
    this.formValuesChanged.emit();
  }

  public selectBuggy(): void {
    this.dialogBuilderFactoryService
      .getBuilder()
      .openSelectGridDialog(SelectBuggyComponent, {
        getBuggy: this.getSelectedBuggy(),
        machineId: this.machine.id,
        machineQualityId: this.machineQuality.id,
        creelId: this.creel.id,
        isPropertyValueForBuggyNameEmpty: this.hasBuggyNameGenerator
      })
      .pipe(takeUntil(this.unSubscribeOnViewDestroy))
      .subscribe((buggy: BuggyForProductionSchedule) => {
        if (buggy) {
          this.buggy = buggy;
          this.updateBuggyFormField();
        }
      });
  }

  public getSelectedBuggy(): BuggyForProductionSchedule {
    return this.additionalOptionsForm.value.buggy;
  }

  public removeFinishing(): void {
    this.additionalOptionsForm.controls.finishing.setValue(null);
  }

  public hasChosenFinishing(): boolean {
    return !AssertionUtils.isNullOrUndefined(this.additionalOptionsForm.value?.finishing);
  }

  public selectFinishing(): void {
    this.dialogBuilderFactoryService
      .getBuilder()
      .openSelectGridDialog(SelectFinishingComponent, {
        machineQualityId: this.machineQuality.id
      })
      .pipe(takeUntil(this.unSubscribeOnViewDestroy))
      .subscribe((selectedOverviewListFinishing: OverviewListFinishing) => {
        if (!isUndefined(selectedOverviewListFinishing)) {
          const finishing = FinishingForProductionSchedule.fromOverviewListFinishing(selectedOverviewListFinishing);
          const isCurrentFinishingEqualToSelectedFinishing: boolean = isEqual(this.getSelectedFinishing(), finishing);
          this.additionalOptionsForm.controls.finishing.patchValue(finishing, {emitEvent: !isCurrentFinishingEqualToSelectedFinishing});

          if (!isCurrentFinishingEqualToSelectedFinishing) {
            this.additionalOptionsForm.markAsDirty();
          }
        }
      });
  }

  public getSelectedFinishing(): FinishingForProductionSchedule {
    return !AssertionUtils.isNullOrUndefined(this.additionalOptionsForm.controls.finishing) ? this.additionalOptionsForm.value.finishing : null;
  }

  public hasChosenPatternForFreeZone(): boolean {
    return !AssertionUtils.isNullOrUndefined(this.additionalOptionsForm.value?.freeZone?.pattern);
  }

  public selectPatternForFreeZone(): void {
    this.openDialogForZone('freeZone', {
      patternType: PatternType.FREE_ZONE_PATTERNS,
      addPatternCommand: this.addFreeZonePattern.execute(this.machineQuality.id),
      patternTranslationKey: 'TEXTILE_DATA.MACHINE_QUALITY.PATTERN_OR_DRAWING',
      allowPatternCreation: true,
      weaveStructureId: this.machineQuality.weaveStructure.id,
      machineQualityId: this.machineQuality.id
    });
  }

  public getSelectedPatternForFreeZone(): Pattern {
    return this.additionalOptionsForm.value?.freeZone?.pattern;
  }

  public hasChosenPatternForRestZone(): boolean {
    return !isEmpty(this.additionalOptionsForm.get(ProductionScheduleInitialPreparationsAdditionalOptionsComponent.restZonePatternFormControlName).value);
  }

  public selectPatternForRestZone(): void {
    const colorSetId: number = first(this.coloredYarnSets).coloredYarnSet.overviewListColorSet.id;

    this.openDialogForZone('restZone', {
      patternType: PatternType.REST_ZONE_PATTERNS,
      addPatternCommand: this.addRestZonePattern.execute({machineQualityId: this.machineQuality.id, colorSetId}),
      patternTranslationKey: 'TEXTILE_DATA.MACHINE_QUALITY.PATTERN_OR_DRAWING',
      patternItemTranslationKey: 'GENERAL.PATTERN.DRAWINGS',
      allowPatternCreation: true,
      weaveStructureId: this.machineQuality.weaveStructure.id,
      selectedMenuItemIndex: 1,
      machineQualityId: this.machineQuality.id,
      colorSetId
    });
  }

  public getSelectedPatternForRestZone(): Pattern {
    return this.additionalOptionsForm.get(ProductionScheduleInitialPreparationsAdditionalOptionsComponent.restZonePatternFormControlName).value;
  }

  public getInitialFreeZone(): InitialFreeZone {
    let initialFreeZone: InitialFreeZone;

    if (this.machine.canHaveRestAndFreeZonesAndPathLabels()) {
      const isAdditionalOptionsFormDefined = !isUndefined(this.additionalOptionsForm);

      const technicalWidthOfFreeZoneLeftInDents: number = isAdditionalOptionsFormDefined ? this.additionalOptionsForm.value.freeZone.leftTechnicalWidthInDents : null;
      const commercialWidthOfFreeZoneLeftInMM: number = multiply(technicalWidthOfFreeZoneLeftInDents, this.factorForConversionFromDentsToMillimeters);
      const freeZoneLeftUseAsMaximum: boolean = isAdditionalOptionsFormDefined && this.additionalOptionsForm.value.freeZone.freeZoneLeftUseAsMaximum;

      const technicalWidthOfFreeZoneInBetweenInDents: number = isAdditionalOptionsFormDefined ? this.additionalOptionsForm.value.freeZone.inBetweenTechnicalWidthInDents : null;
      const commercialWidthOfFreeZoneInBetweenInMM: number = multiply(technicalWidthOfFreeZoneInBetweenInDents, this.factorForConversionFromDentsToMillimeters);
      const freeZoneInBetweenUseAsMaximum: boolean = isAdditionalOptionsFormDefined && this.additionalOptionsForm.value.freeZone.freeZoneInBetweenUseAsMaximum;

      const technicalWidthOfFreeZoneRightInDents: number = isAdditionalOptionsFormDefined ? this.additionalOptionsForm.value.freeZone.rightTechnicalWidthInDents : null;
      const commercialWidthOfFreeZoneRightInMM: number = multiply(technicalWidthOfFreeZoneRightInDents, this.factorForConversionFromDentsToMillimeters);
      const freeZoneRightUseAsMaximum: boolean = isAdditionalOptionsFormDefined && this.additionalOptionsForm.value.freeZone.freeZoneRightUseAsMaximum;

      initialFreeZone = new InitialFreeZone(
        {
          commercialWidthInMM: commercialWidthOfFreeZoneLeftInMM,
          technicalWidthInDents: technicalWidthOfFreeZoneLeftInDents,
          useAsMaximum: freeZoneLeftUseAsMaximum
        },
        {
          commercialWidthInMM: commercialWidthOfFreeZoneInBetweenInMM,
          technicalWidthInDents: technicalWidthOfFreeZoneInBetweenInDents,
          useAsMaximum: freeZoneInBetweenUseAsMaximum
        },
        {
          commercialWidthInMM: commercialWidthOfFreeZoneRightInMM,
          technicalWidthInDents: technicalWidthOfFreeZoneRightInDents,
          useAsMaximum: freeZoneRightUseAsMaximum
        },
        this.getSelectedPatternForFreeZone()
      );
    }

    return initialFreeZone;
  }

  public getInitialRestZone(): InitialRestZone {
    let initialRestZone: InitialRestZone;

    if (this.machine.canHaveRestAndFreeZonesAndPathLabels()) {
      let restZonePosition: RestZonePosition;
      let userDefinedRestZonePosition: UserDefinedRest;

      if (this.isRestZoneConfigurable) {
        restZonePosition = this.additionalOptionsForm.get(ProductionScheduleInitialPreparationsAdditionalOptionsComponent.restZonePositionFormControlName)?.value;

        if (isEqual(restZonePosition, RestZonePosition.USER_DEFINED)) {
          userDefinedRestZonePosition = {
            position: this.additionalOptionsForm.value.restZone.userDefinedRest.position,
            technicalWidthInDents: this.additionalOptionsForm.value.restZone.userDefinedRest.technicalWidthInDents
          };
        }
      }

      initialRestZone = new InitialRestZone(restZonePosition, null, null, this.getSelectedPatternForRestZone(), userDefinedRestZonePosition);
    }

    return initialRestZone;
  }

  public canShowInputForUserDefinedWidthOfRestZone(): boolean {
    const restZonePosition = this.additionalOptionsForm.get(ProductionScheduleInitialPreparationsAdditionalOptionsComponent.restZonePositionFormControlName).value;
    return !isUndefined(this.additionalOptionsForm) && isEqual(restZonePosition, RestZonePosition.USER_DEFINED);
  }

  public canShowRestZonePosition(): boolean {
    return this.isRestZoneConfigurable && !isNil(this.additionalOptionsForm.get(ProductionScheduleInitialPreparationsAdditionalOptionsComponent.restZonePositionFormControlName));
  }

  public getEarliestPossibleDueDate(): DateTime {
    return DateTime.now();
  }

  public getDueDate(): Date {
    return this.additionalOptionsForm.getRawValue().dueDate?.toJSDate();
  }

  public getComment(): string {
    return this.additionalOptionsForm.value.comment;
  }

  public showFinishingDetails(event: MouseEvent): void {
    this.dialogBuilderFactoryService.getBuilder().withClass('reposition-dialog').withWidth('312px').withMaxHeight('100%').openDialog(RepositionDialogComponent, {
      finishing: this.additionalOptionsForm.value.finishing,
      component: FinishingDetailsComponent,
      sourceElement: event.target,
      positionOfDialog: PositionOfDialog.RIGHT
    });
  }

  public hasScheduleDueDatePermission(): boolean {
    return !isNil(this.currentSubscription) && this.currentSubscription?.hasPermission(this.scheduleDueDatePermission);
  }

  public canShowErrorForFormControl(errorCode: string, formControlName: string): boolean {
    return canShowErrorForErrorCodeAndFormControlForFormGroup(errorCode, formControlName, this.additionalOptionsForm);
  }

  public getRestZonePositionTranslationKey(restZonePosition: RestZonePosition): string {
    return `PRODUCTION_ORDER.CUSTOM_SETTINGS.${RestZonePosition[restZonePosition]}`;
  }

  private setFormFields(): void {
    this.additionalOptionsForm = this.formBuilder.group({});

    this.addFormFieldForBuggy();
    this.addFormFieldForFinishing();
    this.addFormFieldForDueDate();
    this.addFormFieldsForComment();

    if (this.machine.canHaveRestAndFreeZonesAndPathLabels()) {
      this.addFormFieldsForFreeZone();
      this.addFormFieldsForRestZone();
    }

    if (this.areAllFieldsDisabled) {
      this.additionalOptionsForm.controls.buggy.disable();
      this.additionalOptionsForm.controls.finishing.disable();
      this.additionalOptionsForm.controls.dueDate.disable();
      this.additionalOptionsForm.controls.comment.disable();
      this.additionalOptionsForm.controls.freeZone.disable();
      this.additionalOptionsForm.controls.restZone.disable();
    }

    this.additionalOptionsFormValueChangesSubject = this.additionalOptionsForm.valueChanges.pipe(takeUntil(this.unSubscribeOnViewDestroy)).subscribe(() => {
      this.notifyChangesToInitialPreparationsForm();
    });
  }

  private addFormFieldForBuggy(): void {
    this.additionalOptionsForm.addControl('buggy', this.formBuilder.control(null));
    this.updateBuggyFormField();
    this.watchValueChangesForFormFields('buggy', true);
  }

  private addFormFieldForFinishing(): void {
    this.additionalOptionsForm.addControl('finishing', this.formBuilder.control({value: null, disabled: this.canDisableFinishing()}));
    this.updateFinishingFormField();
    this.watchValueChangesForFormFields('finishing');
  }

  private canDisableFinishing(): boolean {
    return !this.currentSubscription?.hasPermission(Permission.CHANGE_INDIVIDUAL_FINISHING) && !this.productionSchedulePlan.hasNoPaths(this.productionSchedule);
  }

  private addFormFieldForDueDate(): void {
    const dueDate: DateTime = !isNil(this.dueDate) ? DateTime.fromJSDate(this.dueDate) : null;
    this.additionalOptionsForm.addControl('dueDate', this.formBuilder.control({value: dueDate, disabled: !this.hasScheduleDueDatePermission()}));
  }

  private addFormFieldsForFreeZone(): void {
    const freeZoneFormBuilderGroup: FreeZoneForm = this.formBuilder.group({
      pattern: this.formBuilder.control(null, Validators.required),
      leftTechnicalWidthInDents: this.formBuilder.control(null, Validators.required),
      freeZoneLeftUseAsMaximum: this.formBuilder.control(null),
      inBetweenTechnicalWidthInDents: this.formBuilder.control(null, Validators.required),
      freeZoneInBetweenUseAsMaximum: this.formBuilder.control(null),
      rightTechnicalWidthInDents: this.formBuilder.control(null, Validators.required),
      freeZoneRightUseAsMaximum: this.formBuilder.control(null)
    });

    this.additionalOptionsForm.addControl('freeZone', freeZoneFormBuilderGroup);
    this.updateFreeZoneFormFields();
    this.watchValueChangesForFormFields('freeZone');
  }

  private addFormFieldsForRestZone(): void {
    this.additionalOptionsForm.addControl(
      'restZone',
      this.formBuilder.group({
        pattern: this.formBuilder.control(null, Validators.required)
      })
    );

    this.updateFormFieldsForRestZone();
    this.watchValueChangesForFormFields('restZone');
  }

  private updateFormFieldsForRestZone(): void {
    if (this.machine.canHaveRestAndFreeZonesAndPathLabels() && !isNil(this.restZone)) {
      const restZoneFormBuilderGroup = this.additionalOptionsForm.controls.restZone;
      restZoneFormBuilderGroup.controls.pattern.patchValue(this.restZone.pattern);

      if (this.isRestZoneConfigurable) {
        this.updateFormFieldsForConfigurableRestZone(restZoneFormBuilderGroup);
      } else if (!AssertionUtils.isNullOrUndefined(restZoneFormBuilderGroup.controls.position)) {
        restZoneFormBuilderGroup.removeControl('position');
        restZoneFormBuilderGroup.removeControl('userDefinedRest');
      }
    }
  }

  private updateFormFieldsForConfigurableRestZone(restZoneFormBuilderGroup: RestZoneForm): void {
    let restZonePosition: RestZonePosition;

    if (!isNil(this.restZone.position)) {
      restZonePosition = !isNil(this.restZone.commercialWidthInMM) ? RestZonePosition.USER_DEFINED : this.restZone.position;
    } else {
      restZonePosition = this.defaultInitialRestZones.position;
    }

    const userDefinedRestFormBuilderGroup: UserDefinedRestForm = this.formBuilder.group({});

    if (isEqual(restZonePosition, RestZonePosition.USER_DEFINED)) {
      let userDefinedRestTechnicalWidthInDents: number;
      let userDefinedRestPosition: RestZonePosition = RestZonePosition.LEFT;

      if (!isNil(this.restZone.userDefinedRest)) {
        userDefinedRestTechnicalWidthInDents = this.restZone.userDefinedRest.technicalWidthInDents;
        userDefinedRestPosition = this.restZone.userDefinedRest.position;
      }

      userDefinedRestFormBuilderGroup.addControl('technicalWidthInDents', this.formBuilder.control(userDefinedRestTechnicalWidthInDents, [Validators.required, Validators.min(0)]));
      userDefinedRestFormBuilderGroup.addControl('position', this.formBuilder.control(userDefinedRestPosition));
    }

    restZoneFormBuilderGroup.addControl('position', this.formBuilder.control(restZonePosition));
    restZoneFormBuilderGroup.addControl('userDefinedRest', userDefinedRestFormBuilderGroup);

    this.setValueWatcherForRestZonePosition();
  }

  private addFormFieldsForComment(): void {
    this.additionalOptionsForm.addControl('comment', this.formBuilder.control(this.comment));
  }

  private watchValueChangesForFormFields(controlName: string, generateName: boolean = false): void {
    this.additionalOptionsForm
      .get(controlName)
      .valueChanges.pipe(takeUntil(this.unSubscribeOnViewDestroy), distinctUntilChanged(), debounceTime(500))
      .subscribe(() => {
        this.formValuesChanged.emit();

        if (generateName) {
          this.generateName.emit();
        }
      });
  }

  private setValueWatcherForRestZonePosition(): void {
    this.additionalOptionsForm
      .get(ProductionScheduleInitialPreparationsAdditionalOptionsComponent.restZonePositionFormControlName)
      .valueChanges.pipe(takeUntil(this.unSubscribeOnViewDestroy))
      .subscribe((restZonePosition: RestZonePosition) => {
        const userDefinedRestFormGroup = this.additionalOptionsForm.controls.restZone.controls.userDefinedRest;

        if (restZonePosition === RestZonePosition.USER_DEFINED) {
          userDefinedRestFormGroup.addControl('technicalWidthInDents', this.formBuilder.control(null, Validators.required));
          userDefinedRestFormGroup.addControl('position', this.formBuilder.control(RestZonePosition.LEFT));
        } else {
          userDefinedRestFormGroup.removeControl('technicalWidthInDents');
          userDefinedRestFormGroup.removeControl('position');
        }
      });
  }

  private openDialogForZone(zone: string, dialogData: SelectPatternComponentMatDialogConfigDataInterface<Pattern>): void {
    this.dialogBuilderFactoryService
      .getBuilder()
      .openSelectGridDialog<SelectPatternComponentMatDialogConfigDataInterface<Pattern>, Pattern>(SelectPatternComponent, dialogData)
      .pipe(takeUntil(this.unSubscribeOnViewDestroy))
      .subscribe((pattern: Pattern | undefined) => {
        if (pattern) {
          const isCurrentPatternEqualToSelectedPattern: boolean = isEqual(this.additionalOptionsForm.get(`${zone}.pattern`).value, pattern);

          if (zone === 'restZone') {
            this.restZoneLoaded.emit();
          } else if (zone === 'freeZone') {
            this.freeZoneLoaded.emit();
          }

          this.additionalOptionsForm.controls[zone].controls.pattern.setValue(pattern, {emitEvent: !isCurrentPatternEqualToSelectedPattern});

          if (!isCurrentPatternEqualToSelectedPattern) {
            this.additionalOptionsForm.markAsDirty();
          }
        }
      });
  }

  private notifyChangesToInitialPreparationsForm(): void {
    if (!this.additionalOptionsFormValueChangesSubject.closed && this.additionalOptionsForm.dirty) {
      this.addProductionSchedule.notifyChangesToInitialPreparationsForm(true);
      this.additionalOptionsFormValueChangesSubject.unsubscribe();
    }
  }

  private updateFreeZoneFormFields(): void {
    if (this.machine.canHaveRestAndFreeZonesAndPathLabels() && !isNil(this.freeZone)) {
      this.additionalOptionsForm.controls.freeZone.patchValue({
        pattern: this.freeZone.pattern,
        leftTechnicalWidthInDents: this.freeZone.left.technicalWidthInDents,
        freeZoneLeftUseAsMaximum: this.freeZone.left.useAsMaximum,
        inBetweenTechnicalWidthInDents: this.freeZone.inBetween.technicalWidthInDents,
        freeZoneInBetweenUseAsMaximum: this.freeZone.inBetween.useAsMaximum,
        rightTechnicalWidthInDents: this.freeZone.right.technicalWidthInDents,
        freeZoneRightUseAsMaximum: this.freeZone.right.useAsMaximum
      });
    }
  }

  private updateFinishingFormField(): void {
    this.additionalOptionsForm.controls.finishing.setValue(this.finishing);
  }

  private updateBuggyFormField(): void {
    this.additionalOptionsForm.controls.buggy.setValue(this.buggy);
  }

  private shouldUpdateRestZoneFormFields(changes: SimpleChanges): boolean {
    return ('restZone' in changes && !changes.restZone.isFirstChange()) || ('isRestZoneConfigurable' in changes && !changes.isRestZoneConfigurable.isFirstChange());
  }

  private checkHasBuggyNameGenerator(): void {
    this.httpBuggyCustomSettings
      .get()
      .pipe(takeUntil(this.unSubscribeOnViewDestroy))
      .subscribe((buggyCustomSettings: CustomSettings) => {
        this.hasBuggyNameGenerator = buggyCustomSettings?.nameGenerator?.parts?.length > 0;
      });
  }
}
